import { api } from '../../lib/api';
import type { PortalPublishPayload } from '../../lib/portalLibrary/staffView';
import type { HostedReportSummary } from '../../types/hostedReport';
import type {
  PortalAudienceReach,
  PortalAudienceRule,
  PortalLibraryCategory,
  PortalLibraryCategoryRow,
  PortalLibraryEntry,
  PortalLibrarySection,
  PortalLibraryStaffRow,
} from '../../types/portalLibrary';

// Client portal library — the STAFF API. ⚠️ There is no client-facing call in
// this file: the portal's own read is Round 2b.

export async function listPortalLibrary(): Promise<PortalLibraryStaffRow[]> {
  const res = await api<{ rows: PortalLibraryStaffRow[] }>('/api/portal-library');
  return res.rows;
}

export async function previewReach(
  audience: PortalAudienceRule[],
  section: PortalLibrarySection,
): Promise<{ reach: PortalAudienceReach; refusal: string | null }> {
  return api('/api/portal-library/reach', {
    method: 'POST',
    body: JSON.stringify({ audience, section }),
  });
}

/**
 * "Remove from portal" (Round 6 §1), keyed by the HOSTED REPORT. Deletes its
 * tile, or clears a stranded marker when the tile is already gone.
 */
export async function removeReportFromPortal(
  reportId: string,
): Promise<{ removed: 'tile' | 'marker' }> {
  return api(`/api/portal-library/remove-report/${encodeURIComponent(reportId)}`, {
    method: 'POST',
  });
}

export async function listPortalLibraryTags(section: PortalLibrarySection): Promise<string[]> {
  const res = await api<{ tags: string[] }>(
    `/api/portal-library/tags?section=${encodeURIComponent(section)}`,
  );
  return res.tags;
}

export type PublishArgs = PortalPublishPayload & {
  title: string;
  accountId: string | null;
  filename: string;
  html: string;
};

export async function publishToPortal(args: PublishArgs): Promise<{
  entry: PortalLibraryEntry;
  report: HostedReportSummary;
  reach: PortalAudienceReach;
}> {
  return api('/api/portal-library', { method: 'POST', body: JSON.stringify(args) });
}

/**
 * Publish an EXISTING hosted report (Round 2c) — same core as `publishToPortal`
 * on the server, with the report supplied instead of a file.
 */
export type AdoptArgs = PortalPublishPayload & {
  title: string;
  hostedReportId: string;
  /** ⚠️ Only ever true because someone ticked "Remove the password" — optional since Round 4; false keeps it on the shareable link. */
  clearPassword: boolean;
};

export async function adoptToPortal(args: AdoptArgs): Promise<{
  entry: PortalLibraryEntry;
  report: HostedReportSummary;
  reach: PortalAudienceReach;
}> {
  return api('/api/portal-library/adopt', { method: 'POST', body: JSON.stringify(args) });
}

export type UpdateEntryArgs = {
  title?: string;
  summary?: string | null;
  audience?: PortalAudienceRule[];
  tags?: string[];
  period?: string | null;
  categoryId?: string | null;
  visibleFromMs?: number;
  visibleUntil?: { action: 'set'; atMs: number } | { action: 'never' };
};

export async function updatePortalEntry(
  id: string,
  args: UpdateEntryArgs,
): Promise<PortalLibraryEntry> {
  const res = await api<{ entry: PortalLibraryEntry }>(`/api/portal-library/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(args),
  });
  return res.entry;
}

export async function deletePortalEntry(id: string): Promise<void> {
  await api(`/api/portal-library/${id}`, { method: 'DELETE' });
}

// ---------- categories (Round 4 §6) ----------

export async function listPortalCategories(): Promise<PortalLibraryCategoryRow[]> {
  const res = await api<{ categories: PortalLibraryCategoryRow[] }>('/api/portal-library/categories');
  return res.categories;
}

export async function createPortalCategory(name: string): Promise<PortalLibraryCategory> {
  const res = await api<{ category: PortalLibraryCategory }>('/api/portal-library/categories', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
  return res.category;
}

export async function renamePortalCategory(id: string, name: string): Promise<void> {
  await api(`/api/portal-library/categories/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });
}

/** The COMPLETE order, every id once. */
export async function reorderPortalCategories(ids: string[]): Promise<void> {
  await api('/api/portal-library/categories/reorder', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

/** ⚠️ Refused by the server (409, naming the count) while items use it. */
export async function deletePortalCategory(id: string): Promise<void> {
  await api(`/api/portal-library/categories/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ---------- thumbnails (Round 4 §4) ----------

export async function uploadPortalThumbnail(
  id: string,
  file: { filename: string; contentType: string; dataBase64: string },
): Promise<PortalLibraryEntry> {
  const res = await api<{ entry: PortalLibraryEntry }>(`/api/portal-library/${id}/thumbnail`, {
    method: 'PUT',
    body: JSON.stringify(file),
  });
  return res.entry;
}

export async function removePortalThumbnail(id: string): Promise<PortalLibraryEntry> {
  const res = await api<{ entry: PortalLibraryEntry }>(`/api/portal-library/${id}/thumbnail`, {
    method: 'DELETE',
  });
  return res.entry;
}
